<template>
  <div class="view">
    <div class="space-y-3 flex-1 min-w-0 max-w-75 overflow-scroll">
      <ChatList
        class="chat-list"
        :selected="chat ? chat.id : null"
        @select="selectChat"
      />
    </div>

    <div class="chat-view" v-if="loaded && chat">
      <div class="controls">
        <template v-if="chat.ticket">
          <div class="status" v-if="assigned">
            Тикет назначен на {{ assignedToName }}
          </div>
          <div class="status" v-else>Новый тикет</div>

          <div class="flex justify-between">
            <uiButton
              class="control"
              color="grey"
              @click="toggleMoveTicket"
              v-if="assignedToMe || $isGod()"
            >
              <template v-if="!movingTicket"> Передать </template>
              <template v-else>
                Кому?
                <div class="supports">
                  <div>
                    <div
                      class="support"
                      v-for="s in supports"
                      :key="s.id"
                      @click="moveTicket(s)"
                    >
                      {{ s.name }} {{ s.surname }}
                    </div>
                  </div>
                </div>
              </template>
            </uiButton>
            <uiButton
              class="control"
              color="grey"
              @click="mergeTicket"
              v-if="assignedToMe || $isGod()"
            >
              Объединить
            </uiButton>

            <uiButton
              class="control"
              color="grey"
              @click="closeTicket"
              v-if="assignedToMe || $isGod()"
            >
              Закрыть
            </uiButton>
            <uiButton
              class="control"
              color="grey"
              @click="takeTicket()"
              v-else-if="!assigned"
            >
              Взять тикет
            </uiButton>
          </div>
        </template>
        <div class="status" v-else>У чата нет тикета</div>
      </div>

      <ChatView
        :id="chat.id"
        :assigned="!!assigned"
        :assignedToMe="!!assignedToMe"
        ref="chatview"
      />

      <div class="grid py-4 grid-cols-2">
        <div class="flex items-center">
          <kbd class="key"> ↵ </kbd>
          <span class="text-xs opacity-50"> – Новая строка </span>
        </div>

        <div class="flex items-center justify-end">
          <kbd class="key"> ⇧ </kbd>
          <kbd class="key"> ↵ </kbd>
          <span class="text-xs opacity-50"> – Отправить </span>
        </div>
      </div>
    </div>

    <div class="side" v-if="chat">
      <uiPreload v-if="!user" />
      <template v-else>
        <div class="pets">
          <h3 v-if="$isGod()">
            <router-link :to="'/users/' + user.id">
              {{ userFullname }}
            </router-link>
          </h3>
          <h3 v-else>{{ userFullname }}</h3>
          <div class="info global-id">{{ user.id }}</div>
          <div class="info">{{ user.phone }}</div>
          <div class="info">С нами {{ userAge }}</div>

          <template v-if="userLastSession">
            <div class="info">Видели {{ userLastSession.time }} назад</div>
            <div class="info" v-if="userLastSession.city">
              <b>{{ userLastSession.city }}</b>
            </div>
          </template>

          <template v-if="$isGod() && user.petMediaCount > 0">
            <uiButton
              color="grey"
              style="margin-top: 10px"
              @click="$router.push('/petmedia/' + user.id)"
            >
              Фоточки ({{ user.petMediaCount }})
            </uiButton>
            <uiDevider />
          </template>

          <uiFold :title="`Сессии (${sessionsCount})`" v-if="$isGod()">
            <UserSessions
              :id="user.id"
              @loaded="sessionsCount = $event"
              @lastsession="userLastSession = $event"
            />
          </uiFold>

          <uiFold :title="`Сканы (${scansCount})`" v-if="$isGod()">
            <UserScans :id="user.id" @loaded="scansCount = $event" />
          </uiFold>

          <div class="info" style="text-align: center" v-if="!pets.length">
            Нет питомцев
          </div>
          <template v-else>
            <uiDropdown
              :options="pets"
              v-model="pet"
              label="Питомец"
              optionsLabel="name"
              v-if="pets.length > 1"
            />
            <PetCard v-if="pet" :id="pet.id" @remove="onPetRemove" />
          </template>
        </div>

        <div class="events">
          <PetEvents :petId="pet.id" v-if="pet" />
        </div>
      </template>
    </div>

    <div class="loading" v-if="loading" />
  </div>
</template>

<script>
import ChatList from '@/components/ChatList'
import ChatView from '@/components/ChatView'
import PetCard from '@/components/PetCard'
import UserBonuses from '@/components/UserBonuses'
import UserSessions from '@/components/UserSessions'
import UserScans from '@/components/UserScans'
import PetEvents from '@/components/PetEvents'
import Repository from '@/repository'
import Petrovich from 'petrovich'
import { API } from '@/config'
import { formatDistanceToNow } from 'date-fns'
import { ru } from 'date-fns/locale'

export default {
  name: 'ChatsPage',
  components: {
    ChatList,
    ChatView,
    PetCard,
    UserBonuses,
    UserSessions,
    UserScans,
    PetEvents
  },
  props: {
    id: String
  },
  data () {
    return {
      loaded: false,
      loading: false,
      chat: null,
      pet: null,
      movingTicket: false,
      startingConsultation: false,
      sessionsCount: 0,
      scansCount: 0,
      userLastSession: null
    }
  },
  computed: {
    me () {
      return this.$store.getters.user
    },
    assigned () {
      return this.chat.assignedTo
    },
    assignedToMe () {
      if (!this.assigned) return
      let id = this.assigned['id'] || this.assigned
      return this.me.id === id
    },
    assignedTo () {
      if (!this.assigned) return
      let id = this.assigned['id'] || this.assigned
      return this.$store.getters.admins.find(a => a.id === id)
    },
    assignedToName () {
      if (!this.assigned) return
      if (this.assignedToMe) return 'меня'
      let u = this.assignedTo
      let t = Petrovich(
        { first: u.name, last: u.surname, gender: u.gender || 'male' },
        'accusative'
      )
      return t.first + ' ' + t.last
    },
    supports () {
      return this.$store.getters.admins
        .filter(a => a.roles.includes('support'))
        .filter(a => a.id !== this.me.id)
    },
    chats () {
      return this.$store.getters.chats
    },
    pets () {
      if (!this.user) return
      return this.$store.getters.pets.filter(p => this.user.pets.includes(p.id))
    },
    user () {
      if (!this.chat) return
      return this.$store.getters.itemById({
        path: 'users',
        id: this.chat.client.id
      })
    },
    userAge () {
      return formatDistanceToNow(new Date(this.user.createdAt), {
        locale: ru
      })
    },
    userFullname () {
      if (!this.user) return ''
      return [
        this.user.roles.includes('love') && '❤️',
        this.user.name,
        this.user.surname,
        this.user.midname
      ]
        .filter(Boolean)
        .join(' ')
    }
  },
  watch: {
    id (nv, pv) {
      if (!nv || nv === pv) return
      this.tryOpenChat()
    }
  },
  created () {
    if (this.id) this.tryOpenChat()
    this.$events.$on('chatAndTicketUpdated', this.chatAndTicketUpdated)
  },
  destroyed () {
    this.$events.$off('chatAndTicketUpdated', this.chatAndTicketUpdated)
  },
  methods: {
    selectChat (chat) {
      this.$router.push('/chats/' + chat.id)
    },
    async tryOpenChat () {
      let chat = this.chats.find(c => c.id == this.id)
      if (!chat) chat = await Repository.loadChat(this.id)
      if (!chat) return this.$notify('Не можем найти такой чат')
      this.onChatSelect(chat)
    },
    async onChatSelect (chat) {
      this.loaded = false
      this.chat = chat
      await Repository.loadUser(chat.client.id)
      await Repository.loadPets(this.user.pets)
      if (this.pets.length) this.pet = this.pets[0]
      this.loaded = true
    },
    async chatAndTicketUpdated (e) {
      if (!this.chat) return
      if (this.chat.id !== e.chat.id) return
      this.chat = e.chat
      this.$refs.chatview.loadMessages(false)
    },
    toggleMoveTicket () {
      this.movingTicket = !this.movingTicket
    },
    async moveTicket (user) {
      let t = Petrovich(
        {
          first: user.name,
          last: user.surname,
          gender: user.gender || 'male'
        },
        'dative'
      )
      let confirm = await this.$confirm(`Передаём тикет ${t.first} ${t.last}?`)
      if (!confirm) return

      this.takeTicket(user.id)
    },
    async takeTicket (userId) {
      if (!userId) {
        let confirm = await this.$confirm('Берём этот тикет?')
        if (!confirm) return
      }

      if (this.loading) return
      this.loading = true

      let { data } = await this.$http.post(API.TICKETS.take, {
        id: this.chat.ticket,
        user: userId
      })
      if (!data || data.err) {
        this.loading = false
        this.$notify(data.err)
        return
      }

      this.$store.commit('updateInArray', {
        path: 'chats',
        data: data.chat
      })
      this.$store.commit('updateInArray', {
        path: 'tickets',
        data: data.ticket
      })
      this.chat = data.chat

      this.loading = false
    },
    updateBonuses (e) {
      let fresh = JSON.parse(JSON.stringify(this.user))
      fresh.bonuses = e
      this.$store.commit('updateInArray', { path: 'users', data: fresh })
    },
    async mergeTicket () {
      let confirm = await this.$confirm('Это точно продолжение старого тикета?')
      if (!confirm) return

      if (this.loading) return
      this.loading = true

      let { data } = await this.$http.post(API.TICKETS.merge, {
        id: this.chat.ticket
      })

      if (!data || data.err) {
        this.loading = false
        this.$notify(data.err)
        return
      }

      this.$store.commit('removeFromArray', {
        path: 'tickets',
        data: this.chat.ticket
      })
      // this.$store.commit('updateInArray', { path: 'chats', data: data.chat })
      // this.$store.commit('updateInArray', { path: 'tickets', data: data.ticket })
      this.chat = data.chat

      this.loading = false
    },
    async closeTicket () {
      let confirm = await this.$confirm('Закрываем?')
      if (!confirm) return
      this.loading = true

      let payload = {
        ticket: this.chat.ticket
      }

      let { data } = await this.$http.post(API.TICKETS.close, payload)

      if (!data || data.err) {
        this.$notify(data.err)
        return (this.loading = false)
      }

      this.$store.commit('updateInArray', {
        path: 'chats',
        data: data.chat
      })
      this.$store.commit('updateInArray', {
        path: 'tickets',
        data: data.ticket
      })
      this.chat = data.chat

      this.loading = false
    },
    async startConsultation () {
      if (this.startingConsultation) return
      this.startingConsultation = true

      let payload = { id: this.user.id }
      let { data } = await this.$http.post(API.USERS.startConsultation, payload)
      if (!data || data.err) {
        this.$notify(data.err)
        return (this.startingConsultation = false)
      }
      this.startingConsultation = false
    },
    onPetRemove (id) {
      this.pet = null
      this.$store.commit('removeFromArray', { path: 'pets', data: id })
      if (this.pets.length) this.pet = this.pets[0]
    }
  }
}
</script>

<style lang="postcss" scoped>
.key {
  @apply rounded-md flex bg-gray-200 h-5 text-xs mr-1 leading-none text-gray-600 w-5 box-border items-center justify-center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}
</style>

<style lang="less" scoped>
@import '../assets/app.less';

.view {
  display: flex;
  // padding: 0 20px;
}

.chat-view {
  width: fit-content;
  display: flex;
  flex-direction: column;
  // flex: 1 1 auto;
  width: 360px;
  flex-shrink: 0;

  .status {
    font-size: 14px;
    opacity: 0.5;
    text-align: center;
    padding-bottom: 12px;
  }

  .controls {
    width: 100%;
    padding-bottom: 12px;
    position: relative;
    z-index: 1;

    .control {
      width: fit-content;
      padding: 0 20px;
      position: relative;
    }

    .supports {
      position: absolute;
      width: 200px;
      // height: fit-content;
      min-height: 200px;
      top: 100%;
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 5px 30px rgba(0, 0, 0, 0.2);
      padding: 10px 20px;
      overflow: scroll;
      z-index: 5;
    }

    .support {
      padding: 5px 0;
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }
    }
  }
}

.side {
  display: flex;
  flex: 1 1 0%;

  .info {
    font-size: 11px;
    opacity: 0.5;
  }
}

.pets {
  padding: 0 20px;
  padding-top: 10px;
  border-right: 2px solid @grey;
  flex: 1;
  height: calc(100vh - 60px);
  overflow: hidden;
  overflow-y: auto;
}

.events {
  flex: 1;
  padding: 0 20px;
  height: calc(100vh - 50px);
  overflow: hidden;
  overflow-y: auto;
}

.loading {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0.5;
  z-index: 100;
}
</style>
